import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import PageHeader from "../components/PageHeader";
import { fetchPatients } from "../../core/redux/slices/patientSlice";
import { fetchInvoices } from "../../core/redux/slices/invoiceSlice";
import { fetchBanks } from "../../core/redux/slices/bankSlice";
import { addInvoicePayment } from "../../core/api/invoiceService";
import { PAYMENT_MODES } from "../../core/constants/paymentModes";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");

const BillWiseReceiptForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const patients = useSelector((s) => s.patients.list);
  const invoices = useSelector((s) => s.invoices.list);
  const banks = useSelector((s) => s.bank.list);

  const [patientId, setPatientId] = useState("");
  const [paymentDate, setPaymentDate] = useState(today());
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [bankId, setBankId] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [allocations, setAllocations] = useState({});
  const [saving, setSaving] = useState(false);
  const [printReceipt, setPrintReceipt] = useState(null);

  useEffect(() => {
    dispatch(fetchPatients());
    dispatch(fetchInvoices());
    dispatch(fetchBanks());
  }, [dispatch]);

  const bankOptions = banks.map((b) => ({ value: b.bankID || b.id, label: b.bankName }));
  const patientOptions = patients.map((p) => ({
    value: p.patientID || p.id,
    label: `${p.patientName} — ${p.mobileNumber || ""} (OP: ${p.opNumber || "—"})`,
  }));
  const patientName = (id) => patients.find((p) => (p.patientID || p.id) === id)?.patientName || "—";

  // No server-side "pending bills by patient" endpoint for invoices exists (unlike
  // /api/SupplierPayment/pending-bills/{id}) — derive it client-side from the already
  // fetched invoice list, same pattern InvoiceList.jsx uses for date filtering.
  const pendingInvoices = useMemo(() => {
    if (!patientId) return [];
    return invoices.filter((inv) => (inv.patientID ?? inv.patientId) === patientId && Number(inv.balanceAmount || 0) > 0);
  }, [invoices, patientId]);

  const handlePatientChange = (opt) => {
    setPatientId(opt?.value || "");
    setAllocations({});
  };

  useEffect(() => {
    const alloc = {};
    pendingInvoices.forEach((inv) => { alloc[inv.id] = ""; });
    setAllocations(alloc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const setAlloc = (invId, value) => setAllocations((prev) => ({ ...prev, [invId]: value }));

  const totalAllocated = Object.values(allocations).reduce((s, v) => s + Number(v || 0), 0);

  // Fills each pending invoice's full balance, matching the supplier-side "pay everything"
  // convenience action (there's no amount-based autofill endpoint for patient invoices).
  const autoFillOldestFirst = () => {
    const alloc = {};
    pendingInvoices.forEach((inv) => { alloc[inv.id] = Number(inv.balanceAmount || 0); });
    setAllocations(alloc);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!patientId) { Swal.fire("Required", "Please select a patient.", "warning"); return; }
    const toPay = pendingInvoices
      .filter((inv) => Number(allocations[inv.id] || 0) > 0)
      .map((inv) => ({ invoiceID: inv.id, invoiceNumber: inv.invoiceNumber || inv.id, amount: Number(allocations[inv.id]) }));
    if (!toPay.length) { Swal.fire("Required", "Allocate payment to at least one invoice.", "warning"); return; }
    const overpaid = toPay.find((t) => {
      const inv = pendingInvoices.find((i) => i.id === t.invoiceID);
      return t.amount > Number(inv?.balanceAmount || 0) + 0.01;
    });
    if (overpaid) { Swal.fire("Invalid Amount", "An invoice's allocated amount exceeds its balance.", "warning"); return; }

    setSaving(true);
    try {
      for (const t of toPay) {
        await addInvoicePayment({
          invoiceID: t.invoiceID,
          paymentDate,
          paymentMode,
          amount: t.amount,
          referenceNo,
          bankId,
        });
      }
      setPrintReceipt({
        patientName: patientName(patientId),
        paymentDate,
        paymentMode,
        referenceNo,
        lines: toPay.map((t) => ({ invoiceNumber: t.invoiceNumber, amount: t.amount })),
        total: totalAllocated,
      });
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Payment recorded", showConfirmButton: false, timer: 2000 });
      setAllocations({});
      setReferenceNo("");
      setBankId("");
      dispatch(fetchInvoices());
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to record payment against one or more invoices."), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <style>{`
          .receipt-print-area { display: none; }
          @media print {
            .header, #sidebar, .on-screen-content { display: none !important; }
            .page-wrapper { margin: 0 !important; padding: 0 !important; }
            .receipt-print-area { display: block; max-width: 600px; margin: 40px auto; font-family: Arial, sans-serif; color: #000; }
            .receipt-print-area table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            .receipt-print-area th, .receipt-print-area td { border: 1px solid #000; padding: 8px 10px; }
            .receipt-print-area .receipt-header { text-align: center; margin-bottom: 20px; }
            .receipt-print-area .meta-row { display: flex; justify-content: space-between; margin-bottom: 6px; }
            .receipt-print-area .total-row td { font-weight: bold; }
          }
        `}</style>

        <div className="on-screen-content">
          <PageHeader title="Add Bill wise Receipt" subtitle="Receive payment against one or more pending patient invoices" />

          <form onSubmit={handleSubmit}>
            <div className="card mb-3">
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-lg-4 mb-2">
                    <label className="form-label">Patient <span className="text-danger">*</span></label>
                    <Select
                      classNamePrefix="react-select"
                      options={patientOptions}
                      value={patientOptions.find((o) => o.value === patientId) || null}
                      onChange={handlePatientChange}
                      placeholder="Search by name or mobile..."
                      isClearable
                      isSearchable
                    />
                  </div>
                  <div className="col-lg-3 mb-2">
                    <label className="form-label">Payment Date</label>
                    <input type="date" className="form-control" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                  </div>
                  <div className="col-lg-2 mb-2">
                    <label className="form-label">Payment Mode</label>
                    <Select
                      classNamePrefix="react-select"
                      options={PAYMENT_MODES}
                      value={PAYMENT_MODES.find((o) => o.value === paymentMode) || null}
                      onChange={(opt) => setPaymentMode(opt?.value || "Cash")}
                    />
                  </div>
                  {paymentMode !== "Cash" && (
                    <div className="col-lg-2 mb-2">
                      <label className="form-label">Bank</label>
                      <Select
                        classNamePrefix="react-select"
                        options={bankOptions}
                        value={bankOptions.find((o) => o.value === bankId) || null}
                        onChange={(opt) => setBankId(opt?.value || "")}
                        placeholder="Select bank"
                        isClearable
                      />
                    </div>
                  )}
                  <div className="col-lg-2 mb-2">
                    <label className="form-label">Reference No.</label>
                    <input className="form-control" value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder="UTR / Txn Id" />
                  </div>
                </div>
              </div>
            </div>

            {patientId && (
              <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Pending Invoices</h5>
                  {pendingInvoices.length > 0 && (
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={autoFillOldestFirst}>
                      Fill Full Balance (All)
                    </button>
                  )}
                </div>
                <div className="card-body p-0">
                  {pendingInvoices.length === 0 ? (
                    <p className="text-muted text-center py-4 mb-0">No pending (unpaid/partially paid) invoices for this patient.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Invoice #</th>
                            <th>Date</th>
                            <th className="text-end">Invoice Amount</th>
                            <th className="text-end">Paid</th>
                            <th className="text-end">Balance</th>
                            <th style={{ width: 150 }} className="text-end">Receive Now (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingInvoices.map((inv) => (
                            <tr key={inv.id}>
                              <td>{inv.invoiceNumber || inv.id}</td>
                              <td>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString("en-IN") : "—"}</td>
                              <td className="text-end">₹{fmt(inv.netTotal)}</td>
                              <td className="text-end">₹{fmt(inv.paidAmount)}</td>
                              <td className="text-end text-danger fw-semibold">₹{fmt(inv.balanceAmount)}</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm text-end"
                                  min={0}
                                  max={inv.balanceAmount}
                                  step="0.01"
                                  value={allocations[inv.id] ?? ""}
                                  onChange={(e) => setAlloc(inv.id, e.target.value)}
                                  placeholder="0.00"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="table-light">
                            <td colSpan={5} className="text-end fw-bold">Total Receipt</td>
                            <td className="text-end fw-bold">₹{fmt(totalAllocated)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="d-flex gap-2 justify-content-end mb-4">
              <button type="button" className="btn btn-cancel" onClick={() => navigate("/invoices/bill-wise-receipt")}>
                Back to List
              </button>
              {printReceipt && (
                <button type="button" className="btn btn-outline-primary" onClick={() => window.print()}>
                  Print Receipt
                </button>
              )}
              <button type="submit" className="btn btn-submit" disabled={saving || totalAllocated <= 0}>
                {saving ? "Saving..." : `Receive ₹${fmt(totalAllocated)}`}
              </button>
            </div>
          </form>
        </div>

        {printReceipt && (
          <div className="receipt-print-area">
            <div className="receipt-header">
              <h4>Payment Receipt</h4>
            </div>
            <div className="meta-row"><span>Patient: {printReceipt.patientName}</span><span>Date: {formatDate(printReceipt.paymentDate)}</span></div>
            <div className="meta-row"><span>Payment Mode: {printReceipt.paymentMode}</span><span>Reference: {printReceipt.referenceNo || "—"}</span></div>
            <table>
              <thead>
                <tr><th>Invoice #</th><th className="text-end">Amount (₹)</th></tr>
              </thead>
              <tbody>
                {printReceipt.lines.map((l, idx) => (
                  <tr key={idx}><td>{l.invoiceNumber}</td><td className="text-end">{fmt(l.amount)}</td></tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row"><td>Total</td><td className="text-end">{fmt(printReceipt.total)}</td></tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillWiseReceiptForm;
